import type { ValueChainEdge, ValueChainGraph, ValueChainNode } from '@/types/valueChain';

const ST_SOURCES = [
  {
    label: 'ST BiCMOS Technology',
    url: 'https://www.st.com/content/st_com/en/about/innovation-and-technology/bicmos.html',
    host: 'st.com',
  },
  {
    label: 'ST & Starlink Partnership',
    url: 'https://newsroom.st.com/media-center/press-item.html/t4741.html',
    host: 'newsroom.st.com',
  },
];

function node(partial: ValueChainNode): ValueChainNode {
  return partial;
}

function edge(
  from: string,
  to: string,
  relation: ValueChainEdge['relation'],
  supplyChain = false,
): ValueChainEdge {
  return {
    id: `${from}__${relation}__${to}`,
    from,
    to,
    relation,
    supplyChain,
  };
}

const rfChainNodes: ValueChainNode[] = [
  node({
    id: 'starlink-terminal',
    name: 'Starlink Terminal',
    type: 'Product / System',
    description:
      'Outdoor electronically steered user terminal that talks to Starlink satellites, authenticates as a valid dish, converts incoming power, and runs terminal firmware. Public evidence covers Gen2, Rev3/V2, and Rev4-style hardware; official manufacturing BOMs are not public.',
    manufacturer: 'SpaceX / Starlink (final product); STMicroelectronics for custom RF/compute silicon',
    keyMaterials: 'Phased-array aperture, custom RF silicon, enclosure, power/data interface',
    notes:
      'The complete terminal is a SpaceX/Starlink product. Public teardown evidence does not disclose the contract manufacturer or full assembly supply chain.',
    industry: 'Satellite Communications',
    company: 'SpaceX',
    country: 'United States',
    sources: ST_SOURCES,
  }),
  node({
    id: 'phased-array-antenna',
    name: 'Phased Array Antenna',
    type: 'Assembly / Module',
    description:
      'Sealed outdoor RF aperture: hundreds to ~1,000+ patch elements, FEMs, beamformer ASICs, thermal/shielding structures, and the antenna PCB that forms and steers Ku-band beams.',
    manufacturer: 'SpaceX / Starlink assembly; STMicroelectronics custom RF chips',
    keyMaterials: 'Patch elements, RF PCB, FEM ICs, beamformer ASICs, radome',
    notes:
      'Teardowns identify the assembly and mechanical/thermal structures, but exact element count, full RF chain per element, and revision-specific part numbers are not fully public.',
    industry: 'Satellite Communications',
    company: 'SpaceX',
    country: 'United States',
    sources: ST_SOURCES,
  }),
  node({
    id: 'front-end-module',
    name: 'Front End Module (FEM)',
    type: 'Component',
    description:
      'Pulsar / PulsarAD custom RF front-end module that sits close to the patch elements and performs the last analog RF work: PA, LNA, analog phase shift, and T/R path control.',
    manufacturer: 'Likely STMicroelectronics / SpaceX-Starlink custom silicon',
    keyMaterials: 'Custom BiCMOS RF die, package/PLP, PA and LNA blocks',
    notes:
      'High confidence that ST supplies Starlink custom phased-array/BiCMOS RF chips at category level; medium confidence that every Pulsar/PulsarAD package specifically maps to ST, because no public catalog part number exists.',
    industry: 'Semiconductors',
    company: 'STMicroelectronics',
    country: 'France',
    sources: ST_SOURCES,
  }),
  node({
    id: 'power-amplifier',
    name: 'Power Amplifier (PA)',
    type: 'Component',
    description:
      'Transmit amplifier block inside Pulsar/PulsarAD. Amplifies outbound Starlink uplink RF energy before it is coupled into the TX patch antenna path. Not publicly shown as a discrete catalog PA.',
    manufacturer: 'Integrated inside likely STMicroelectronics / SpaceX custom Pulsar FEM',
    keyMaterials: 'SiGe HBT gain devices, BiCMOS process, 1.8 V PA rail',
    notes:
      'Oleg Kutkov states each Pulsar FEM contains a PA, LNA, and analog phase shifter. No public Pulsar datasheet, die photo, or PA-stage schematic is available.',
    industry: 'Semiconductors',
    company: 'STMicroelectronics',
    country: 'France',
    sources: ST_SOURCES,
  }),
  node({
    id: 'rf-gain-transistor',
    name: 'RF Gain Transistor',
    type: 'Device',
    description:
      'Active RF power/gain device stack used by the PA output stage. Provides PA output power, linearity, and efficiency inside the custom FEM die rather than as a separately purchased transistor.',
    manufacturer: 'STMicroelectronics BiCMOS / SiGe BiCMOS platform, co-designed with SpaceX',
    keyMaterials: 'SiGe HBT, BiCMOS bipolar device family',
    notes:
      'ST states its BiCMOS technology is used for Starlink phased-array antennas. Transistor topology, device sizes, and PA class are not public.',
    industry: 'Semiconductors',
    company: 'STMicroelectronics',
    country: 'France',
    sources: ST_SOURCES,
  }),
  node({
    id: 'sige-hbt',
    name: 'SiGe HBT',
    type: 'Device',
    description:
      'Silicon-germanium heterojunction bipolar transistor option within ST BiCMOS. This is the high-speed/high-gain RF device family used for Starlink phased-array RF silicon, not a catalog discrete.',
    manufacturer: 'STMicroelectronics',
    keyMaterials: 'SiGe:C heterojunction stack, emitter-base-collector structure',
    notes:
      'The only named public supplier for this restricted SiGe HBT device option is STMicroelectronics. SpaceX/Starlink is the co-design customer/system owner.',
    industry: 'Semiconductors',
    company: 'STMicroelectronics',
    country: 'France',
    sources: [
      ...ST_SOURCES,
      {
        label: 'IEEE Bipolar/BiCMOS Circuits',
        url: 'https://doi.org/10.1109/bipol.1999.803538',
        host: 'doi.org',
      },
    ],
  }),
  node({
    id: 'sige-c-heterojunction-stack',
    name: 'SiGe:C Heterojunction Stack',
    type: 'Layer / Structure',
    description:
      'Vertical HBT material stack: SiGe:C intrinsic base, silicon emitter, collector module, and the emitter-base / base-collector junctions that give the transistor its RF speed-to-noise advantage.',
    manufacturer: 'STMicroelectronics',
    keyMaterials: 'SiGe:C base, Si emitter, collector module, boron base doping',
    notes:
      'ST publicly names SiGe-C HBT, thick copper RF metal, high-resistivity substrate, and isolation features, but does not publish the Starlink/Pulsar die cross-section.',
    industry: 'Semiconductors',
    company: 'STMicroelectronics',
    country: 'France',
    sources: ST_SOURCES,
  }),
  node({
    id: 'sige-c-intrinsic-base',
    name: 'SiGe:C Intrinsic Base',
    type: 'Layer / Structure',
    description:
      'Intrinsically thin, boron-doped silicon-germanium-carbon base layer in the HBT used to achieve high-frequency performance in ST’s BiCMOS technology.',
    manufacturer: 'STMicroelectronics (co-design/manufacturing)',
    keyMaterials: 'Si, Ge, C (composition), B (dopant)',
    notes:
      'Exact composition, thickness, and process details are not public for Starlink/Pulsar.',
    industry: 'Semiconductors',
    company: 'STMicroelectronics',
    country: 'France',
    sources: ST_SOURCES,
  }),
  node({
    id: 'silicon',
    name: 'Silicon (Si)',
    type: 'Material',
    description:
      'Host semiconductor constituent of the SiGe:C HBT base system. Highest functional importance as the matrix of the intrinsic base layer.',
    manufacturer: 'STMicroelectronics is the identified BiCMOS chip/process manufacturer; no upstream silicon-material supplier is publicly disclosed.',
    notes: 'Cost rank is not publicly disclosed. Ordering in source analysis is by functional indispensability, not cost.',
    industry: 'Materials',
    company: 'STMicroelectronics',
    country: 'France',
    sources: ST_SOURCES,
  }),
  node({
    id: 'germanium',
    name: 'Germanium (Ge)',
    type: 'Material',
    description:
      'Alloying constituent that creates the SiGe heterojunction base for high-speed RF operation. Primary bandgap-engineering element in the intrinsic base.',
    manufacturer: 'STMicroelectronics is the identified BiCMOS chip/process manufacturer; no upstream germanium-material supplier is publicly disclosed.',
    notes: 'Exact Ge percentage/profile for Starlink/Pulsar is undisclosed.',
    industry: 'Materials',
    company: 'STMicroelectronics',
    country: 'France',
    sources: ST_SOURCES,
  }),
  node({
    id: 'carbon',
    name: 'Carbon (C)',
    type: 'Material',
    description:
      'Low-concentration base constituent that suppresses boron out-diffusion and stabilizes the thin base doping profile during thermal processing.',
    manufacturer: 'STMicroelectronics is the identified BiCMOS chip/process manufacturer; no upstream carbon-material supplier is publicly disclosed.',
    notes: 'Small material fraction but critical for base-profile stability. Cost is process-control embedded.',
    industry: 'Materials',
    company: 'STMicroelectronics',
    country: 'France',
    sources: ST_SOURCES,
  }),
  node({
    id: 'boron',
    name: 'Boron (B)',
    type: 'Chemical / Dopant',
    description:
      'P-type dopant in the SiGe:C intrinsic base. Carbon is used to suppress boron diffusion and preserve a steep base profile needed for high-speed HBT behavior.',
    manufacturer: 'STMicroelectronics; no external dopant supplier is public.',
    notes:
      'Necessary for base doping. Fourteenth-order analysis treats Si, Ge, and C as the direct base-layer constituents and boron as the dopant, not a composition element.',
    industry: 'Materials',
    company: 'STMicroelectronics',
    country: 'France',
    sources: [
      ...ST_SOURCES,
      {
        label: 'IEEE Bipolar/BiCMOS Circuits',
        url: 'https://doi.org/10.1109/bipol.1999.803538',
        host: 'doi.org',
      },
    ],
  }),
  node({
    id: 'stmicroelectronics',
    name: 'STMicroelectronics',
    type: 'Company',
    description:
      'Best-supported manufacturer and co-design partner for Starlink custom phased-array/BiCMOS RF chips. ST says products are manufactured in France and packaged/tested in Malaysia and Malta, including a panel-level packaging process with Starlink as lead customer.',
    manufacturer: 'STMicroelectronics',
    industry: 'Semiconductors',
    company: 'STMicroelectronics',
    country: 'France',
    sources: ST_SOURCES,
  }),
  node({
    id: 'spacex-starlink',
    name: 'SpaceX / Starlink',
    type: 'Company',
    description:
      'System owner, RF architecture owner, custom-chip co-designer/customer, firmware/calibration owner, and final product integrator. Not publicly shown as the wafer fab for Pulsar/PulsarAD.',
    manufacturer: 'SpaceX',
    industry: 'Satellite Communications',
    company: 'SpaceX',
    country: 'United States',
    sources: ST_SOURCES,
  }),
];

const rfChainEdges: ValueChainEdge[] = [
  edge('starlink-terminal', 'phased-array-antenna', 'HAS_PART'),
  edge('phased-array-antenna', 'front-end-module', 'HAS_PART'),
  edge('front-end-module', 'power-amplifier', 'HAS_PART'),
  edge('power-amplifier', 'rf-gain-transistor', 'HAS_PART'),
  edge('rf-gain-transistor', 'sige-hbt', 'IS_A'),
  edge('sige-hbt', 'sige-c-heterojunction-stack', 'HAS_STRUCTURE'),
  edge('sige-c-heterojunction-stack', 'sige-c-intrinsic-base', 'HAS_PART'),
  edge('sige-c-intrinsic-base', 'silicon', 'COMPOSED_OF'),
  edge('sige-c-intrinsic-base', 'germanium', 'COMPOSED_OF'),
  edge('sige-c-intrinsic-base', 'carbon', 'COMPOSED_OF'),
  edge('sige-c-intrinsic-base', 'boron', 'DOPED_WITH'),
  edge('starlink-terminal', 'spacex-starlink', 'MANUFACTURED_BY', true),
  edge('front-end-module', 'stmicroelectronics', 'MANUFACTURED_BY', true),
  edge('power-amplifier', 'stmicroelectronics', 'MANUFACTURED_BY', true),
  edge('rf-gain-transistor', 'stmicroelectronics', 'MANUFACTURED_BY', true),
  edge('sige-hbt', 'stmicroelectronics', 'MANUFACTURED_BY', true),
  edge('sige-c-heterojunction-stack', 'stmicroelectronics', 'MANUFACTURED_BY', true),
  edge('sige-c-intrinsic-base', 'stmicroelectronics', 'MANUFACTURED_BY', true),
];

const kitNodes: ValueChainNode[] = [
  node({
    id: 'standard-kit',
    name: 'Standard / Standard 4X Kit',
    type: 'Product / System',
    description:
      'Main consumer/residential terminal kit: dish handles the satellite link; router handles local WiFi/Ethernet; power supply and cable power and connect the system.',
    manufacturer: 'SpaceX / Starlink',
    industry: 'Satellite Communications',
    company: 'SpaceX',
    country: 'United States',
  }),
  node({
    id: 'kit-antenna',
    name: 'Electronic Phased-Array Antenna',
    type: 'Assembly / Module',
    description: 'Core satellite link. Electronically steered phased-array terminal with ~110° field of view on Standard/Enterprise kits.',
    manufacturer: 'SpaceX / Starlink; ST-heavy custom silicon inside',
    industry: 'Satellite Communications',
    company: 'SpaceX',
    country: 'United States',
  }),
  node({
    id: 'kit-router',
    name: 'Router 3 / Gen 3 Router',
    type: 'Component',
    description: 'Indoor WiFi/Ethernet router bundled with Standard kits. Public evidence is stronger for Gen2 MediaTek silicon than for every Gen3 part number.',
    manufacturer: 'SpaceX / Starlink; MediaTek SoC in analyzed generations',
    industry: 'Networking',
    company: 'SpaceX',
    country: 'United States',
  }),
  node({
    id: 'kit-cable',
    name: 'Starlink Cable',
    type: 'Component',
    description: 'Proprietary outdoor cable carrying power and data into the dish while maintaining seal and shielding.',
    manufacturer: 'Unknown from public evidence',
    industry: 'Satellite Communications',
    company: 'SpaceX',
    country: 'United States',
  }),
  node({
    id: 'kit-psu',
    name: 'Power Supply',
    type: 'Component',
    description: 'Indoor power supply that feeds the proprietary high-voltage PoE-style link to the outdoor terminal.',
    manufacturer: 'Unknown from public evidence; power semiconductors on related boards include ST, TI, and MPS',
    industry: 'Power Electronics',
    company: 'SpaceX',
    country: 'United States',
  }),
  node({
    id: 'kit-kickstand',
    name: 'Standard Kickstand',
    type: 'Component',
    description: 'Basic installation support for the Standard kit antenna.',
    manufacturer: 'Unknown from public evidence',
    industry: 'Satellite Communications',
    company: 'SpaceX',
    country: 'United States',
  }),
  node({
    id: 'kit-ac-cable',
    name: 'AC Power Cable',
    type: 'Component',
    description: 'Mains cable for the indoor power supply.',
    manufacturer: 'Unknown from public evidence',
    industry: 'Satellite Communications',
    company: 'SpaceX',
    country: 'United States',
  }),
];

const dishNodes: ValueChainNode[] = [
  node({
    id: 'uta',
    name: 'User Terminal Antenna',
    type: 'Product / System',
    description:
      'A sealed, outdoor, software-defined phased-array computer: weatherproof RF aperture, hundreds of FEMs, multiple beamformer ASICs, custom SoC, secure identity, GNSS, dense DC/DC tree, and firmware/calibration.',
    manufacturer: 'SpaceX / Starlink',
    industry: 'Satellite Communications',
    company: 'SpaceX',
    country: 'United States',
    sources: ST_SOURCES,
  }),
  node({
    id: 'radome',
    name: 'Radome / Aperture Stack',
    type: 'Assembly / Module',
    description: 'Weather-facing RF-transparent cover and RF aperture stack that protects the patch array while passing Ku-band energy.',
    manufacturer: 'Unknown production supplier',
    industry: 'Satellite Communications',
    company: 'SpaceX',
    country: 'United States',
  }),
  node({
    id: 'patch-array',
    name: 'Patch Antenna Array',
    type: 'Assembly / Module',
    description: 'Hundreds to ~1,000+ patch elements that radiate or receive the combined phased signal through the radome.',
    manufacturer: 'Unknown PCB / antenna-stack suppliers',
    industry: 'Satellite Communications',
    company: 'SpaceX',
    country: 'United States',
  }),
  node({
    id: 'shiraz',
    name: 'Shiraz Beamformer ASICs',
    type: 'Component',
    description:
      'Digital beamformer ICs that command FEM chains and translate desired beam direction into per-sector phase/amplitude/RF-control states. Rev3/V2 public mapping: 16 DBF ICs; Rev4 drops to six next-generation beamformers.',
    manufacturer: 'Best-supported: STMicroelectronics / SpaceX custom silicon',
    industry: 'Semiconductors',
    company: 'STMicroelectronics',
    country: 'France',
    sources: ST_SOURCES,
  }),
  node({
    id: 'pulsar-fem',
    name: 'Pulsar / PulsarAD FEM',
    type: 'Component',
    description: 'Near-element custom RF FEM containing PA, LNA, analog phase shifter, and RF I/O to the beamformer.',
    manufacturer: 'Likely STMicroelectronics / SpaceX-Starlink custom silicon',
    industry: 'Semiconductors',
    company: 'STMicroelectronics',
    country: 'France',
    sources: ST_SOURCES,
  }),
  node({
    id: 'catson-soc',
    name: 'Custom SoC / Catson',
    type: 'Component',
    description:
      'Main terminal computer inside the dish: custom quad-core Cortex-A53, boots firmware, controls RF/beamforming hardware, manages telemetry and power sequencing.',
    manufacturer: 'STMicroelectronics, custom-made for SpaceX / Starlink',
    industry: 'Semiconductors',
    company: 'STMicroelectronics',
    country: 'France',
    sources: ST_SOURCES,
  }),
  node({
    id: 'stsafe',
    name: 'STSAFE-A110',
    type: 'Component',
    description: 'Secure element for device identity and authentication. Stores/uses UUID/certificate material and participates in mutual authentication.',
    manufacturer: 'STMicroelectronics',
    industry: 'Semiconductors',
    company: 'STMicroelectronics',
    country: 'France',
    sources: ST_SOURCES,
  }),
];

const femNodes: ValueChainNode[] = [
  node({
    id: 'pulsar-body',
    name: 'Pulsar / PulsarAD FEM',
    type: 'Component',
    description:
      'Complete custom RF front-end module near the patch elements: amplification, switching/path control, phase adjustment, and interface to the Shiraz beamformer.',
    manufacturer: 'Likely STMicroelectronics / SpaceX-Starlink custom silicon',
    industry: 'Semiconductors',
    company: 'STMicroelectronics',
    country: 'France',
    sources: ST_SOURCES,
  }),
  node({
    id: 'fem-lna',
    name: 'Low-Noise Amplifier',
    type: 'Component',
    description: 'Amplifies weak received satellite signals with minimal added noise before they are passed back toward the beamformer.',
    manufacturer: 'Inside Pulsar/PulsarAD; likely STMicroelectronics / SpaceX custom silicon',
    industry: 'Semiconductors',
    company: 'STMicroelectronics',
    country: 'France',
  }),
  node({
    id: 'fem-pa',
    name: 'Power Amplifier Section',
    type: 'Component',
    description: 'Amplifies outbound transmit RF energy before it reaches the antenna element, enabling the terminal uplink beam.',
    manufacturer: 'Inside Pulsar/PulsarAD; likely STMicroelectronics / SpaceX custom silicon',
    industry: 'Semiconductors',
    company: 'STMicroelectronics',
    country: 'France',
  }),
  node({
    id: 'fem-switch',
    name: 'PA/LNA Switch',
    type: 'Component',
    description: 'Selects or isolates transmit and receive paths so the FEM can handle TX/RX without the wrong path loading or damaging the other.',
    manufacturer: 'Inside Pulsar/PulsarAD; likely STMicroelectronics / SpaceX custom silicon',
    industry: 'Semiconductors',
    company: 'STMicroelectronics',
    country: 'France',
  }),
  node({
    id: 'fem-phase',
    name: 'Analog Phase Shifter',
    type: 'Component',
    description:
      'Applies local analog phase adjustment at or near the antenna element. Additional phase shifting is performed inside the Shiraz beamformer.',
    manufacturer: 'Inside Pulsar/PulsarAD; likely STMicroelectronics / SpaceX custom silicon',
    industry: 'Semiconductors',
    company: 'STMicroelectronics',
    country: 'France',
  }),
  node({
    id: 'fem-rfio',
    name: 'RF I/O to Shiraz',
    type: 'Component',
    description: 'Carries RF signals between the beamformer ASIC and the FEM. Each Shiraz has 16 RFIO channels and associated Pulsar FEMs in Rev3/V2 evidence.',
    manufacturer: 'Implemented by the custom DBF/FEM silicon pair',
    industry: 'Semiconductors',
    company: 'STMicroelectronics',
    country: 'France',
  }),
];

export const VALUE_CHAIN_GRAPHS: ValueChainGraph[] = [
  {
    id: 'starlink-rf-chain',
    name: 'Starlink RF Chain',
    rootId: 'starlink-terminal',
    defaultSelectedId: 'sige-c-intrinsic-base',
    nodes: rfChainNodes,
    edges: rfChainEdges,
  },
  {
    id: 'starlink-kit',
    name: 'Starlink Kit',
    rootId: 'standard-kit',
    defaultSelectedId: 'standard-kit',
    nodes: kitNodes,
    edges: [
      edge('standard-kit', 'kit-antenna', 'HAS_PART'),
      edge('standard-kit', 'kit-router', 'HAS_PART'),
      edge('standard-kit', 'kit-cable', 'HAS_PART'),
      edge('standard-kit', 'kit-psu', 'HAS_PART'),
      edge('standard-kit', 'kit-kickstand', 'HAS_PART'),
      edge('standard-kit', 'kit-ac-cable', 'HAS_PART'),
    ],
  },
  {
    id: 'dish-antenna',
    name: 'Dish Antenna Build',
    rootId: 'uta',
    defaultSelectedId: 'pulsar-fem',
    nodes: dishNodes,
    edges: [
      edge('uta', 'radome', 'HAS_PART'),
      edge('uta', 'patch-array', 'HAS_PART'),
      edge('uta', 'shiraz', 'HAS_PART'),
      edge('uta', 'pulsar-fem', 'HAS_PART'),
      edge('uta', 'catson-soc', 'HAS_PART'),
      edge('uta', 'stsafe', 'HAS_PART'),
    ],
  },
  {
    id: 'pulsar-fem-stack',
    name: 'Pulsar FEM Stack',
    rootId: 'pulsar-body',
    defaultSelectedId: 'fem-pa',
    nodes: femNodes,
    edges: [
      edge('pulsar-body', 'fem-lna', 'HAS_PART'),
      edge('pulsar-body', 'fem-pa', 'HAS_PART'),
      edge('pulsar-body', 'fem-switch', 'HAS_PART'),
      edge('pulsar-body', 'fem-phase', 'HAS_PART'),
      edge('pulsar-body', 'fem-rfio', 'HAS_PART'),
    ],
  },
];

export const DEFAULT_VALUE_CHAIN_GRAPH_ID = 'starlink-rf-chain';
